import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { ThemeProvider } from "./context/ThemeContext";
import LandingPage from "./components/LandingPage";
import LegacyLandingPage from "./components/LegacyLandingPage";
import NewLanding from "./components/NewLanding";
import Login from "./components/Login";
import Signup from "./components/Signup";
import Signup2 from "./components/Signup2";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";
import Dashboard from "./components/dashboard/Dashboard";
import AppStats from "./components/AppStats";
import Pricing from "./components/Pricing";
import DebateMode from "./components/DebateMode";
import OldDebate from "./components/OldDebate";
import FAQPage from "./components/FAQ";
import Settings from "./components/Settings";
import Admin from "./components/Admin";
import UserActionDetails from "./components/UserActionDetails";
import AdminSubscriptionHistory from "./components/AdminSubscriptionHistory";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import About from "./components/About";
import CheckoutSuccess from "./components/CheckoutSuccess";
import CheckoutCancel from "./components/CheckoutCancel";
import CompleteProfile from "./components/CompleteProfile";
import Directory from "./components/Directory";
import AudienceSplitter from "./components/AudienceSplitter";
import LegalQuestion from "./components/LegalQuestion";
import FindAttorney from "./components/FindAttorney";
import DirectoryVerify from "./components/DirectoryVerify";
import DirectoryActivate from "./components/DirectoryActivate";
import MyDirectoryListing from "./components/MyDirectoryListing";
import EditMyListing from "./components/EditMyListing";
import Unsubscribe from "./components/Unsubscribe";
import NotFound from "./components/NotFound";
import "./App.css";
import NetworkConnection from "./components/NetworkConnection.jsx";
import SupportChatWidget from "./components/SupportChatWidget";
import PageViewTracker from "./components/PageViewTracker";

/**
 * ScrollToTop - Resets window scroll position on every route change.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/**
 * Legacy redirect: research chats used to live at /dashboard/:id. That path now
 * belongs to the app stats page, so forward old links to /research/:id.
 */
function LegacyDashboardRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/research/${id}` : "/research"} replace />;
}

// Guard: ensure all rendered components are defined (helps debug invalid element type)
const required = {
  ThemeProvider,
  AuthProvider,
  NotificationProvider,
  NetworkConnection,
  Router,
  LandingPage,
  Login,
  Signup,
  PrivateRoute,
  Dashboard,
  AppStats,
  Pricing,
  DebateMode,
  FAQPage,
  Settings,
  Admin,
  AdminRoute,
};
const undefinedComponent = Object.entries(required).find(
  ([, C]) => C === undefined,
);
if (undefinedComponent) {
  throw new Error(
    `App: "${undefinedComponent[0]}" is undefined. Check export/import (default vs named).`,
  );
}

function App() {
  React.useEffect(() => {

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS) {
      const handleFocusOut = (e) => {
        if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) {
          const viewport = document.querySelector("meta[name='viewport']");
          if (viewport) {
            const originalContent = viewport.getAttribute("content");
            viewport.setAttribute("content", originalContent + ", maximum-scale=1");
            setTimeout(() => {
              viewport.setAttribute("content", originalContent);
            }, 300);
          }
        }
      };
      document.addEventListener("focusout", handleFocusOut);
      return () => document.removeEventListener("focusout", handleFocusOut);
    }
  }, []);

  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <NetworkConnection />
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <div className="App">
                <PageViewTracker />
                <ScrollToTop />
                <Routes>
                  {/* The splitter is the LawFusion.ai homepage: a visitor is
                      asked who they are before being shown either product. The
                      attorney marketing page keeps its own URL. */}
                  <Route path="/" element={<AudienceSplitter />} />
                  <Route path="/attorneys" element={<LandingPage />} />
                  {/* The splitter used to live here; keep old links working. */}
                  <Route path="/start" element={<Navigate to="/" replace />} />
                  <Route path="/ask" element={<LegalQuestion />} />
                  <Route path="/find-attorney" element={<FindAttorney />} />
                  <Route path="/legacy-landing" element={<LegacyLandingPage />} />
                  <Route path="/new-landing" element={<NewLanding />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/signup2" element={<Signup2 />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  {/* /dashboard is now the app stats overview. Old research
                      chat links (/dashboard/:id) redirect to /research/:id so
                      existing bookmarks keep working. */}
                  <Route path="/dashboard/:id" element={<LegacyDashboardRedirect />} />
                  <Route path="/dashboard" element={<AppStats />} />
                  {/* /research — alias for the research workspace.
                      /dashboard is kept live because ~36 internal links,
                      redirects and bookmarks still point at it. */}
                  <Route
                    path="/research/:id"
                    element={
                      // <PrivateRoute>
                      <Dashboard />
                      // </PrivateRoute>
                    }
                  />
                  <Route
                    path="/research"
                    element={
                      // <PrivateRoute>
                      <Dashboard />
                      // </PrivateRoute>
                    }
                  />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route
                    path="/debate-mode/:chatId"
                    element={
                      // <PrivateRoute>
                      <DebateMode />
                      // </PrivateRoute>
                    }
                  />
                  <Route
                    path="/debate-mode"
                    element={
                      // <PrivateRoute>
                      <DebateMode />
                      // </PrivateRoute>
                    }
                  />
                  <Route
                    path="/debate"
                    element={
                      // <PrivateRoute>
                      <DebateMode />
                      // </PrivateRoute>
                    }
                  />
                  <Route
                    path="/oldDebate"
                    element={
                      <PrivateRoute>
                        <OldDebate />
                      </PrivateRoute>
                    }
                  />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/directory" element={<Directory />} />
                  <Route path="/directory/verify" element={<DirectoryVerify />} />
                  <Route path="/directory/activate" element={<DirectoryActivate />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route
                    path="/my-listing"
                    element={
                      <PrivateRoute>
                        <MyDirectoryListing />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/my-listing/edit"
                    element={
                      <PrivateRoute>
                        <EditMyListing />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <PrivateRoute>
                        <Settings />
                      </PrivateRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <AdminRoute>
                        <Admin />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/user-actions/:userId"
                    element={
                      <AdminRoute>
                        <UserActionDetails />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="/admin/subscription-history/:userId"
                    element={
                      <AdminRoute>
                        <AdminSubscriptionHistory />
                      </AdminRoute>
                    }
                  />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-of-service" element={<TermsOfService />} />
                  <Route path="/about" element={<About />} />
                  {/* Legacy Stripe success URL — keep for bookmarks / env misconfig */}
                  <Route path="/checkout-success" element={<CheckoutSuccess />} />
                  <Route path="/stripe/success" element={<CheckoutSuccess />} />
                  <Route path="/stripe/sucess" element={<CheckoutSuccess />} />
                  <Route path="/stripe/cancel" element={<CheckoutCancel />} />
                  <Route path="/checkout-cancel" element={<CheckoutCancel />} />
                  <Route path="/cancel" element={<CheckoutCancel />} />
                  <Route
                    path="/complete-profile"
                    element={
                      <PrivateRoute>
                        <CompleteProfile />
                      </PrivateRoute>
                    }
                  />
                  {/* Catch-all. Must stay last: React Router picks the best
                      match, but keeping it here also keeps the file readable.
                      Without it an unknown URL matched no route at all and
                      rendered a blank page with only the support widget. */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
              <SupportChatWidget />
            </Router>
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
